import { has } from 'lodash-es';
import { includesEnumValue } from '../utils/enumlikeHandlers';
import type { Paths, RequiredProperties } from '../utils/types';
import type { Security } from './Security';
import { InstrumentCompositionEnum, ProductTypeEnum, SyntheticProductTypeEnum } from './types';

const OPTION_STRATEGY_TYPES = [
  SyntheticProductTypeEnum.CallSpread,
  SyntheticProductTypeEnum.PutSpread,
  SyntheticProductTypeEnum.CallCalendarSpread,
  SyntheticProductTypeEnum.PutCalendarSpread,
] as const;

export type SecurityTypes = {
  Spot: RequiredProperties<Security, 'BaseCurrency'>;
  Option: RequiredProperties<Security, 'BaseCurrency' | 'QuoteCurrency' | 'Expiration' | 'StrikePrice' | 'OptionType'>;
  Future: RequiredProperties<Security, 'BaseCurrency' | 'SettleValueType' | 'Expiration'>;
  Perp: RequiredProperties<Security, 'BaseCurrency' | 'SettleValueType'>;
  Multileg: RequiredProperties<Security, 'BaseCurrency' | 'MultilegDetails'>;
  Delta1Multileg: RequiredProperties<Security, 'BaseCurrency' | 'MultilegDetails'>;
  CrossMultileg: RequiredProperties<Security, 'BaseCurrency' | 'MultilegDetails'>;
  CalendarSpread: RequiredProperties<Security, 'BaseCurrency'>;
  OptionStrategy: RequiredProperties<Security, 'BaseCurrency' | 'MultilegDetails'>;
  /** ProductType Basis is legacy */
  Basis: RequiredProperties<Security, 'BaseCurrency' | 'MultilegDetails'>;
  CFD: RequiredProperties<Security, 'BaseCurrency'>;
  ETF: RequiredProperties<Security, 'BaseCurrency'>;
  Compound: RequiredProperties<Security, 'Legs'>;
  PredictionOutcome: Security;
};

export const SecurityTypesUtils: {
  [key in keyof SecurityTypes]: {
    /** Just validates the ProductType(s) on the security, does not check that expected properties are present */
    isProductType: (security: Security | undefined) => boolean;
    /**
     * Validates the product type on the security and that the relevant properties for the product type are also populated.
     * Returns a well typed security based on the product type
     */
    isType: (security: Security | undefined) => security is SecurityTypes[key];
  };
} = {
  Spot: {
    isProductType: security =>
      security?.ProductType === ProductTypeEnum.Spot && security.Composition !== InstrumentCompositionEnum.Synthetic,
    isType: (security): security is SecurityTypes['Spot'] =>
      SecurityTypesUtils.Spot.isProductType(security) && hasProperties(security, ['BaseCurrency']),
  },
  Option: {
    isProductType: sec => sec?.ProductType === ProductTypeEnum.Option,
    isType: (security): security is SecurityTypes['Option'] =>
      SecurityTypesUtils.Option.isProductType(security) &&
      hasProperties(security, ['Expiration', 'StrikePrice', 'QuoteCurrency', 'BaseCurrency', 'OptionType']),
    // TODO(robertz): Add Security.UnderlyingQuoteCurrency, UnderlyingSymbol to check above
  },
  Future: {
    isProductType: sec => sec?.ProductType === ProductTypeEnum.Future,
    isType: (security): security is SecurityTypes['Future'] =>
      SecurityTypesUtils.Future.isProductType(security) &&
      hasProperties(security, ['BaseCurrency', 'SettleValueType', 'Expiration']),
  },
  Perp: {
    isProductType: sec => sec?.ProductType === ProductTypeEnum.PerpetualSwap,
    isType: (security): security is SecurityTypes['Perp'] =>
      SecurityTypesUtils.Perp.isProductType(security) && hasProperties(security, ['BaseCurrency', 'SettleValueType']),
  },
  Multileg: {
    isProductType: security => security?.ProductType === ProductTypeEnum.Synthetic,
    isType: (security): security is SecurityTypes['Multileg'] =>
      SecurityTypesUtils.Multileg.isProductType(security) &&
      hasProperties(security, ['MultilegDetails', 'BaseCurrency']),
  },
  Delta1Multileg: {
    isProductType: security =>
      SecurityTypesUtils.Multileg.isProductType(security) &&
      security?.MultilegDetails?.SyntheticProductType === SyntheticProductTypeEnum.Delta1Spread,
    isType: (security): security is SecurityTypes['Delta1Multileg'] =>
      SecurityTypesUtils.Delta1Multileg.isProductType(security) &&
      hasProperties(security, ['MultilegDetails', 'BaseCurrency']),
  },
  CrossMultileg: {
    isProductType: security =>
      SecurityTypesUtils.Multileg.isProductType(security) &&
      security?.MultilegDetails?.SyntheticProductType === SyntheticProductTypeEnum.Cross,
    isType: (security): security is SecurityTypes['CrossMultileg'] =>
      SecurityTypesUtils.CrossMultileg.isProductType(security) &&
      hasProperties(security, ['MultilegDetails', 'BaseCurrency']),
  },
  CalendarSpread: {
    isProductType: security => security?.ProductType === ProductTypeEnum.CalendarSpread,
    isType: (security): security is SecurityTypes['CalendarSpread'] =>
      SecurityTypesUtils.CalendarSpread.isProductType(security) && hasProperties(security, ['BaseCurrency']),
  },
  OptionStrategy: {
    isProductType: security => {
      if (!security || !security.MultilegDetails?.SyntheticProductType) {
        return false;
      }

      return (
        security?.ProductType === ProductTypeEnum.Synthetic &&
        includesEnumValue(OPTION_STRATEGY_TYPES, security.MultilegDetails.SyntheticProductType)
      );
    },
    isType: (security): security is SecurityTypes['OptionStrategy'] =>
      SecurityTypesUtils['OptionStrategy'].isProductType(security) &&
      hasProperties(security, ['BaseCurrency', 'MultilegDetails']),
  },
  Basis: {
    isProductType: security => {
      if (!security) {
        return false;
      }

      if (security?.ProductType === ProductTypeEnum.Basis) {
        return true;
      }

      if (SecurityTypesUtils.CalendarSpread.isProductType(security)) {
        return true;
      }

      if (
        SecurityTypesUtils.Multileg.isProductType(security) &&
        !SecurityTypesUtils.CrossMultileg.isProductType(security)
      ) {
        return true;
      }

      return false;
    },
    isType: (security): security is SecurityTypes['Basis'] =>
      SecurityTypesUtils.Basis.isProductType(security) && hasProperties(security, ['BaseCurrency', 'MultilegDetails']),
  },
  CFD: {
    isProductType: sec => sec?.ProductType === ProductTypeEnum.CFD,
    isType: (security): security is SecurityTypes['CFD'] =>
      SecurityTypesUtils.CFD.isProductType(security) && hasProperties(security, ['BaseCurrency']),
  },
  ETF: {
    isProductType: sec => sec?.ProductType === ProductTypeEnum.ExchangeTradedFund,
    isType: (security): security is SecurityTypes['ETF'] =>
      SecurityTypesUtils.ETF.isProductType(security) && hasProperties(security, ['BaseCurrency']),
  },
  Compound: {
    isProductType: sec => sec?.ProductType === ProductTypeEnum.Compound,
    isType: (security): security is SecurityTypes['Compound'] =>
      SecurityTypesUtils.Compound.isProductType(security) && hasProperties(security, ['Legs']),
  },
  PredictionOutcome: {
    isProductType: sec => sec?.ProductType === ProductTypeEnum.PredictionOutcome,
    isType: (security): security is SecurityTypes['PredictionOutcome'] =>
      SecurityTypesUtils.PredictionOutcome.isProductType(security),
  },
};

function hasProperties(security: Security | undefined, propertyPaths: Paths<Security>[]): boolean {
  if (!security) {
    return false;
  }

  return !propertyPaths.some(path => !has(security, path));
}
